<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { customerInfoProviderContextToken } from "@/context_tokens";
    import { hasNextPage } from "@/utils/paginationUtils/paginationUtils";
    import WishlistEmptyListState from "@apps/wishlist/components/WishlistEmptyListState.svelte";
    import WishlistSocialShare from "@apps/wishlist/components/WishlistSocialShare/WishlistSocialShare.svelte";
    import { BUTTON_VARIANT } from "@apps/wishlist/components/WishlistTheme/constants";
    import { wishlistThemeContextToken } from "@apps/wishlist/components/WishlistTheme/WIshlistThemeContextToken";
    import { WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES } from "@apps/wishlist/constants/constants";
    import { WishlistsManageApproversModalWidgetDefinition } from "@apps/wishlist/definitions";
    import { WishlistsStorefrontValuesSettingsEntity } from "@apps/wishlist/entities/WishlistsStorefrontValuesSettingsEntity";
    import { WishlistWithItemsEntity } from "@apps/wishlist/entities/WishlistWithItemsEntity";
    import {
        wishlistApplicationLoggerToken,
        wishlistsManageApproversModalToken,
    } from "@apps/wishlist/tokens";
    import {
        WISHLIST_ITEMS_FILTERING_TYPE,
        WISHLIST_ITEMS_SORTING_TYPE,
    } from "@apps/wishlist/types";
    import FormManager from "@lib/FormManager/FormManager";
    import FormManagerController from "@lib/FormManager/FormManagerController.svelte";
    import FormManagerProvider from "@lib/FormManager/FormManagerProvider.svelte";
    import { ValidationStrategy } from "@lib/FormManager/types";
    import yupResolver from "@lib/FormManager/yupResolver";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { CommonModule } from "@modules/common_module/CommonModule";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import { CombinedProductsAndProductsWithQuantityRulesQuery } from "@modules/products/queries/CombinedProductsAndProductsWithQuantityRulesQuery";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import clickOutside from "@ui-utils/actions/clickOutside";
    import Observable from "@ui-utils/Observable.svelte";
    import CN from "classnames";
    import { map } from "rxjs";
    import { createEventDispatcher } from "svelte";
    import { container } from "tsyringe";
    import { object, string } from "yup";

    import ActionItem from "@components/Actions/ActionItem.svelte";
    import ActionsList from "@components/Actions/ActionsList.svelte";
    import ActionsPopover from "@components/Actions/ActionsPopover.svelte";
    import Badge from "@components/Badge/Badge.svelte";
    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Button from "@components/Buttons/Button.svelte";
    import ButtonWithIcon from "@components/Buttons/ButtonWithIcon.svelte";
    import BaseTextInput from "@components/forms/BaseTextInput/BaseTextInput.svelte";
    import Icon from "@components/Icon.svelte";
    import CheckIcon from "@components/icons/CheckIcon.svelte";
    import ChevronIcon from "@components/icons/ChevronIcon.svelte";
    import LockIcon from "@components/icons/LockIcon.svelte";
    import LoadMore from "@components/LoadMore.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import Money from "@components/Money.svelte";
    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";

    import { OpenedWishlistsUIStore } from "../../wishlists_widget/ui_stores/OpenedWishlistsUIStore";

    export let wishlist: WishlistWithItemsEntity;
    export let currentSort: WISHLIST_ITEMS_SORTING_TYPE;
    export let currentFilter: WISHLIST_ITEMS_FILTERING_TYPE =
        WISHLIST_ITEMS_FILTERING_TYPE.all;
    export let productsQuery: ProductsQuery;
    export let combinedProductsAndProductsWithQuantityRulesQuery: CombinedProductsAndProductsWithQuantityRulesQuery;

    export let openedWishlistsUIStore: OpenedWishlistsUIStore;
    export let isSharedList: boolean;
    export let loadMoreStatus: STATUS;
    export let namespace: string;
    export let criticalNamespace: string;
    export let wishlistSettings:
        | WishlistsStorefrontValuesSettingsEntity
        | undefined = undefined;
    export let enabledSocialShareChannels: {
        isEnabled: boolean;
        channelName: WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES;
    }[] = [];
    export let shareWishlistsStatus: STATUS = STATUS.VIRGIN;
    export let sendNotificationToApproversStatus: STATUS = STATUS.VIRGIN;
    export let createDraftOrderStatus: STATUS = STATUS.VIRGIN;
    export let approveWishlistStatus: STATUS = STATUS.VIRGIN;
    export let modalManager: ModalManager;
    export let dynamicImportManager: DynamicImportManager;

    let isEditable = false;
    let isShareLinkCopied = false;

    const customerInfoProvider = getTypedContext(
        customerInfoProviderContextToken
    );

    const isB2b = customerInfoProvider.getIsB2b();

    // B2B permissions logic
    $: canEditWishlist =
        !isB2b ||
        wishlist.isOwner ||
        (wishlistSettings?.isEditListsByCompanyMembersEnabled ?? false);

    const dispatch = createEventDispatcher();
    const TitleFormScheme = object({
        title: string().min(2),
    });
    const logger = container.resolve(wishlistApplicationLoggerToken);
    const urlsBuilder = container.resolve(CommonModule).exports.urlsBuilder;
    const wishlistBranding = getTypedContext(wishlistThemeContextToken);

    const products$ = productsQuery.selectAll();
    const productsWithUpdatedPrices$ =
        combinedProductsAndProductsWithQuantityRulesQuery.getAllProducts();
    let filteredWishlistsCount = 0;

    $: wishlistItems$ = products$.pipe(
        map((products) => {
            const productVariantsMap = new Map<number, Set<number>>();

            for (const product of products) {
                const variantIds = new Set(
                    product.variants.map((variant) => variant.id)
                );
                productVariantsMap.set(product.id, variantIds);
            }

            const wishlistItems = wishlist.items.filter((item) => {
                const variantIds = productVariantsMap.get(item.productId);
                return variantIds?.has(item.variantId) ?? false;
            });

            return wishlistItems;
        })
    );

    $: filteredWishlistItems$ = wishlistItems$.pipe(
        map((wishlistItems) => {
            if (currentFilter !== WISHLIST_ITEMS_FILTERING_TYPE.all) {
                const filteredItems = wishlistItems.filter((item) => {
                    const product = $products$.find(
                        (p) => p.id === item.productId
                    );
                    if (!product) return false;

                    const variant = product.variants.find(
                        (v) => v.id === item.variantId
                    );
                    return variant && variant.availableForSale;
                });

                filteredWishlistsCount = filteredItems.length;
                return filteredItems;
            } else {
                filteredWishlistsCount = wishlistItems.length;
                return wishlistItems;
            }
        })
    );

    $: totalApprovers = wishlist.approvers?.length || 0;
    $: approvedCount =
        wishlist.approvers?.filter((approver) => approver.isApproved)?.length ||
        0;

    $: estimatedTotalPrice = $filteredWishlistItems$.reduce((total, item) => {
        const product = $productsWithUpdatedPrices$.find(
            (p) => p.id === item.productId
        );
        const variant = product?.variants.find((v) => v.id === item.variantId);
        const price = variant?.price?.amount || 0;
        return total + price * item.quantity;
    }, 0);

    $: showSocialShareIcons =
        wishlistSettings?.isShareSeparateWishlistsEnabled &&
        wishlistSettings?.isWishlistSocialShareEnabled &&
        enabledSocialShareChannels.length &&
        $filteredWishlistItems$.length &&
        !wishlist.isPrivate;

    const formManager = new FormManager(
        {
            initialValues: {
                title: wishlist.title,
            },
            unregisterOnUnmount: false,
            resolver: yupResolver(TitleFormScheme),
            validationStrategy: ValidationStrategy.OnChange,
            onSubmit: ({ values }) => {
                if (values) {
                    isEditable = false;
                    handleRenameWishlist(wishlist.id, values.title);
                }
            },
        },
        logger
    );

    let titleInputRef: HTMLInputElement | null = null;
    let isOpened$ = openedWishlistsUIStore.selectWishlistIsOpened(
        wishlist.id,
        true
    );

    const changeShareLinkCopyStatus = () => {
        isShareLinkCopied = true;
        setTimeout(() => (isShareLinkCopied = false), 800);
    };

    const handleToggleEditable = () => {
        isEditable = !isEditable;
    };

    const handleToggle = () => {
        openedWishlistsUIStore.toggleWishlist(wishlist.id);
    };

    const handleDeleteWishlist = (wishlistId: number) => {
        dispatch("deleteWishlist", { wishlistId });
    };

    const handleRenameWishlist = (wishlistId: number, newTitle: string) => {
        dispatch("renameWishlist", { wishlistId, newTitle });
    };

    const handleMakeWishlistDefault = (wishlistId: number) => {
        dispatch("makeWishlistDefault", { wishlistId });
    };

    const togglePrivateWishlist = (wishlistId: number, isPrivate: boolean) => {
        dispatch("togglePrivateWishlist", {
            wishlistId,
            isPrivate,
        });
    };

    const handleLoadMore = (
        limit: number,
        offset: number,
        wishlistId: number,
        currentSort: WISHLIST_ITEMS_SORTING_TYPE
    ) => {
        dispatch("loadMore", {
            limit,
            offset,
            wishlistId,
            orderBy: currentSort,
        });
    };

    const handleApproveWishlist = (wishlistId: number) => {
        dispatch("approveWishlist", { wishlistId });
    };

    const handleSendNotificationToApprovers = (wishlistId: number) => {
        dispatch("sendNotificationToApprovers", { wishlistId });
    };

    const handleCreateDraftOrder = (wishlistId: number) => {
        dispatch("createDraftOrder", { wishlistId });
    };

    const handleDuplicateWishlist = (wishlistId: number) => {
        dispatch("duplicateWishlist", { wishlistId });
    };

    let showConfirmDelete = false;
    let showShareList = false;

    const toggleConfirmDelete = () => {
        showConfirmDelete = !showConfirmDelete;
    };

    const toggleShowShareList = () => {
        showShareList = !showShareList;
    };

    const handleOpenManageApproversModal = async () => {
        await dynamicImportManager.fetch({
            definition: WishlistsManageApproversModalWidgetDefinition,
        });
        setTimeout(() => {
            modalManager.openModal(
                wishlistsManageApproversModalToken,
                undefined,
                {
                    wishlistId: wishlist.id,
                }
            );
        }, 0);
    };

    $: {
        formManager.resetValues({
            title: wishlist.title,
        });
    }

    $: {
        if (isEditable && titleInputRef) {
            titleInputRef.focus();
            titleInputRef.selectionStart = 0;
        }
    }

    $: hasNextPage$ = hasNextPage({
        perPage: wishlist.perPage,
        currentOffset: wishlist.currentOffset,
        totalCount: wishlist.totalCount,
    });

    const i18nModule = container.resolve(I18nModule);

    const save_wishlist_button_accessibility_label$ =
        i18nModule.exports.i18n.selectT(
            criticalNamespace,
            "save_wishlist_button_accessibility_label"
        );
</script>

<div class="gw-wl-page-list">
    <div class="gw-wl-page-list__header">
        <div class="gw-wl-page-title-wrapper">
            <div class="gw-wl-page-list__toggle-icon-wrapper">
                <ButtonWithIcon
                    on:click={handleToggle}
                    ariaLabel={$isOpened$
                        ? "Collapse wishlist"
                        : "Expand wishlist"}
                >
                    <Icon
                        color="#777E90"
                        width={12}
                        height={12}
                        class={$isOpened$
                            ? "gw-toggle-btn gw-toggle-btn-opened"
                            : "gw-toggle-btn gw-toggle-btn-closed"}
                    >
                        <ChevronIcon />
                    </Icon>
                </ButtonWithIcon>
            </div>

            {#if !isEditable}
                <div class="gw-wl-page-list__header-title">
                    <BaseButton
                        class="gw-wl-page-list__header-title-btn"
                        on:click={handleToggle}
                    >
                        {wishlist.title}
                        {#if filteredWishlistsCount}
                            <Badge
                                variant="flat"
                                bold
                                size="lg"
                                backgroundColor="none"
                            >
                                ({filteredWishlistsCount})
                            </Badge>
                        {/if}
                    </BaseButton>
                </div>
            {:else}
                <FormManagerProvider {formManager}>
                    <form on:submit|preventDefault={formManager.submit}>
                        <FormManagerController name="title" let:field>
                            <div class="gw-wl-page-list__title-input-wrapper">
                                <BaseTextInput
                                    bind:ref={titleInputRef}
                                    on:change={field.onChange}
                                    name={field.name}
                                    value={field.value}
                                    class={CN("gw-wl-page-list__title-input", {
                                        "--gw-with-error":
                                            field.errors && field.errors.length,
                                    })}
                                />
                            </div>
                        </FormManagerController>
                    </form>
                </FormManagerProvider>
            {/if}

            {#if !isSharedList}
                {#if isEditable}
                    <ButtonWithIcon
                        on:click={formManager.submit}
                        ariaLabel={$save_wishlist_button_accessibility_label$}
                    >
                        <Icon color="#777E90">
                            <CheckIcon />
                        </Icon>
                    </ButtonWithIcon>
                {/if}
            {/if}

            {#if wishlist.isPrivate && !isEditable}
                <Icon viewBox="0 0 20 20" width={20} height={20}>
                    <LockIcon />
                </Icon>
            {/if}
        </div>

        <div
            class={CN(
                "gw-wl-page-list-header__line",
                showConfirmDelete && "gw-wl-page-list-header__line--denger"
            )}
        />

        {#if showSocialShareIcons}
            <div
                class="gw-wl-page-share-list-wrapper"
                use:clickOutside
                on:clickOutside={() => {
                    showShareList = false;
                }}
            >
                <Button
                    on:click={() => toggleShowShareList()}
                    variant="text"
                    size="small"
                >
                    <Text size="sm" variant="secondary" weight="semibold">
                        <T
                            {namespace}
                            translationKey="share_single_list_button_label"
                        />
                    </Text>
                </Button>

                <div
                    class={CN(
                        "gw-wl-page-share-list",
                        "gw-wl-page-action-overlay",
                        {
                            "gw-wl-page-action-overlay_visible": showShareList,
                        }
                    )}
                >
                    <div class="gw-wl-page-share-list__title">
                        <T
                            {namespace}
                            translationKey="share_single_list_title"
                        />
                    </div>
                    <Observable
                        observable={i18nModule.exports.i18n.selectT(
                            criticalNamespace,
                            "link_copied_label"
                        )}
                        let:value={t_link_copied_label}
                    >
                        <WishlistSocialShare
                            on:share={({ detail: channelName }) => {
                                dispatch("shareWishlists", {
                                    channelName,
                                    linkCopiedCallback:
                                        changeShareLinkCopyStatus,
                                });
                            }}
                            linkCopiedLabel={t_link_copied_label}
                            {isShareLinkCopied}
                            wishlistSocialShareChannels={enabledSocialShareChannels}
                            isShareLoading={shareWishlistsStatus ===
                                STATUS.LOADING}
                            iconsSize="2.375em"
                        />
                    </Observable>
                </div>
            </div>
        {/if}

        {#if !isSharedList && canEditWishlist}
            <ActionsPopover autoClose>
                <Button slot="activator" variant="text" size="small">
                    <Text size="sm" variant="secondary" weight="semibold">
                        <T {namespace} translationKey="options" />
                    </Text>
                </Button>
                <ActionsList>
                    <ActionItem on:click={() => handleToggleEditable()}>
                        <span class="gw-wl-action-item">
                            <T {namespace} translationKey="rename" />
                        </span>
                    </ActionItem>
                    {#if wishlist.isDefault}
                        <ActionItem disabled>
                            <span class="gw-wl-action-item">
                                <T {namespace} translationKey="default_list" />
                            </span>
                        </ActionItem>
                    {:else}
                        <ActionItem
                            on:click={() =>
                                handleMakeWishlistDefault(wishlist.id)}
                        >
                            <span
                                class="gw-wl-action-item"
                                class:disabled={wishlist.isDefault}
                            >
                                <T
                                    {namespace}
                                    translationKey="mark_as_default_list"
                                />
                            </span>
                        </ActionItem>
                    {/if}

                    {#if !isB2b}
                        <ActionItem
                            on:click={() =>
                                togglePrivateWishlist(
                                    wishlist.id,
                                    !wishlist.isPrivate
                                )}
                        >
                            <span class="gw-wl-action-item">
                                {#if wishlist.isPrivate}
                                    <T
                                        {namespace}
                                        translationKey="make_public_list"
                                    />
                                {:else}
                                    <T
                                        {namespace}
                                        translationKey="make_private_list"
                                    />
                                {/if}
                            </span>
                        </ActionItem>
                    {/if}

                    {#if isB2b && wishlistSettings?.isDuplicateWishlistEnabled}
                        <ActionItem
                            on:click={() =>
                                handleDuplicateWishlist(wishlist.id)}
                        >
                            <span class="gw-wl-action-item">
                                <T
                                    {namespace}
                                    translationKey="duplicate_list"
                                />
                            </span>
                        </ActionItem>
                    {/if}

                    {#if isB2b && wishlist.isOwner && wishlistSettings?.isListsApprovalByCompanyMembersEnabled}
                        <ActionItem on:click={handleOpenManageApproversModal}>
                            <span class="gw-wl-action-item">
                                <T
                                    {namespace}
                                    translationKey="manage_approvers"
                                />
                            </span>
                        </ActionItem>
                    {/if}

                    <ActionItem
                        color="#EF466F"
                        on:click={() => toggleConfirmDelete()}
                    >
                        <span class="gw-wl-action-item">
                            <T {namespace} translationKey="delete" />
                        </span>
                    </ActionItem>
                </ActionsList>
            </ActionsPopover>
        {/if}
    </div>

    <div class="gw-wl-page-list-content">
        {#if isB2b && !isSharedList}
            <div class="gw-wl-page-list-b2b" class:gw-opened={$isOpened$}>
                {#if wishlistSettings?.isListsApprovalByCompanyMembersEnabled && (wishlist.isApprover || wishlist.isOwner) && wishlist.sentNotificationsCount}
                    <div class="gw-wl-page-list-b2b__approve-status">
                        <T {namespace} translationKey="approved_count" />:
                        <b>{approvedCount}/{totalApprovers}</b>
                    </div>
                {/if}
                {#if wishlist.isOwner && wishlistSettings?.isListsApprovalByCompanyMembersEnabled && wishlist.approvers?.length}
                    <div class="gw-wl-page-list-b2b__send-notification">
                        <Button
                            on:click={() =>
                                handleSendNotificationToApprovers(wishlist.id)}
                            variant="text"
                            size="large"
                            loading={sendNotificationToApproversStatus ===
                                STATUS.LOADING}
                            disabled={!filteredWishlistsCount}
                            class="gw-wl-button-with-icon"
                        >
                            {#if wishlist.sentNotificationsCount && wishlist.sentNotificationsCount > 0}
                                <T
                                    {namespace}
                                    translationKey="resend_for_approval"
                                />
                            {:else}
                                <T
                                    {namespace}
                                    translationKey="send_for_approval"
                                />
                            {/if}
                        </Button>
                    </div>
                {/if}
                <div class="gw-wl-page-list-b2b__total-price">
                    <T {namespace} translationKey="estimated_total" />:
                    <b>
                        <Money amount={estimatedTotalPrice} />
                    </b>
                </div>
                {#if wishlist.isOwner && wishlistSettings?.isCreateDraftOrderFromListEnabled}
                    <div class="gw-wl-page-list-b2b__create-draft-order">
                        <Button
                            on:click={() => handleCreateDraftOrder(wishlist.id)}
                            variant="text"
                            size="large"
                            loading={createDraftOrderStatus === STATUS.LOADING}
                            disabled={createDraftOrderStatus === STATUS.READY ||
                                (wishlistSettings?.isListsApprovalByCompanyMembersEnabled &&
                                    (totalApprovers === 0 ||
                                        approvedCount < totalApprovers))}
                            class="gw-wl-button-with-icon"
                        >
                            <T
                                {namespace}
                                translationKey={createDraftOrderStatus ===
                                STATUS.READY
                                    ? "draft_order_created"
                                    : "create_draft_order"}
                            />
                        </Button>
                    </div>
                {/if}

                {#if wishlistSettings?.isListsApprovalByCompanyMembersEnabled && wishlist.isApprover && !wishlist.isApproved}
                    <div class="gw-wl-page-list-b2b__approve-section">
                        <div class="gw-wl-page-list-b2b__approve-section__info">
                            <b>
                                <T
                                    {namespace}
                                    translationKey="approve_list_question"
                                />
                            </b>
                            <Text variant="secondary" size="sm">
                                <T
                                    {namespace}
                                    translationKey="approved_count"
                                />
                                {approvedCount}/{totalApprovers}
                            </Text>
                        </div>
                        <div
                            class="gw-wl-page-list-b2b__approve-section__action"
                        >
                            <Button
                                on:click={() =>
                                    handleApproveWishlist(wishlist.id)}
                                loading={approveWishlistStatus ===
                                    STATUS.LOADING}
                            >
                                <T
                                    {namespace}
                                    translationKey="approve_button"
                                />
                            </Button>
                        </div>
                    </div>
                {/if}
            </div>
        {/if}
        <div
            class="gw-wl-page-list__items-wrapper"
            class:gw-opened={$isOpened$}
        >
            {#if !filteredWishlistsCount && !isSharedList}
                <WishlistEmptyListState
                    wishlistIconSettings={wishlistBranding.wishlistIconSettings}
                    {urlsBuilder}
                >
                    <T
                        {namespace}
                        translationKey="empty_state_title"
                        slot="title"
                    />
                    <T
                        {namespace}
                        translationKey="empty_state_description"
                        slot="description"
                    />
                    <T
                        namespace={criticalNamespace}
                        translationKey="browse_products"
                        slot="browseLinkText"
                    />
                </WishlistEmptyListState>
            {/if}

            <ul class="gw-wl-page-list__items">
                <slot filteredWishlistItems={$filteredWishlistItems$} />
            </ul>

            {#if hasNextPage$}
                <LoadMore
                    loading={loadMoreStatus === STATUS.LOADING}
                    on:loadMore={() =>
                        handleLoadMore(
                            wishlist.perPage,
                            wishlist.perPage + wishlist.currentOffset,
                            wishlist.id,
                            currentSort
                        )}
                    class="gw-wl-page-load-more"
                    variant={BUTTON_VARIANT[
                        wishlistBranding.wishlistDrawerAndPageSettings
                            .buttonsStyle
                    ]}
                    size="large"
                >
                    <T {namespace} translationKey="load_more" />
                </LoadMore>
            {/if}
        </div>

        <div
            use:clickOutside
            on:clickOutside={() => {
                showConfirmDelete = false;
            }}
            class={CN(
                "gw-wl-page-delete-confirmation",
                "gw-wl-page-action-overlay",
                {
                    "gw-wl-page-action-overlay_visible": showConfirmDelete,
                }
            )}
        >
            <div class="gw-wl-page-delete-confirmation__title">
                <T
                    {namespace}
                    translationKey="delete_wishlist_confirmation_title"
                />
            </div>
            <div class="gw-wl-page-delete-confirmation__description">
                <T
                    {namespace}
                    translationKey="delete_wishlist_confirmation_description"
                />
            </div>
            <div class="gw-wl-page-delete-confirmation__actions">
                <Button
                    class="gw-wl-page-delete-confirmation__confirm-btn"
                    variant="link"
                    on:click={() => handleDeleteWishlist(wishlist.id)}
                >
                    <T
                        {namespace}
                        translationKey="delete_wishlist_confirmation_confirm_button_text"
                    />
                </Button>
                <Button
                    class="gw-wl-page-delete-confirmation__cancel-btn"
                    variant="link"
                    on:click={toggleConfirmDelete}
                >
                    <T
                        {namespace}
                        translationKey="delete_wishlist_confirmation_cancel_button_text"
                    />
                </Button>
            </div>
        </div>
    </div>
</div>

<style lang="postcss">
    .gw-wl-page-list {
        :global(.gw-empty-state a) {
            color: var(--gw-wl-wishlistDrawerAndPageSettings-linkColor);
        }

        :global(.gw-actions-popover-wrapper .gw-actions-list) {
            min-width: auto;

            :global(.gw-action-item .gw-action-item__btn) {
                justify-content: flex-start;
            }
        }
    }

    .gw-wl-page-list__header {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        padding: 0.5em 0;
    }

    .gw-wl-page-list__header-title {
        font-size: 0.9em;
        font-weight: bold;
    }

    :global(.gw-wl-page-list__header-title-btn) {
        font-size: inherit;
        font-weight: inherit;
        text-transform: inherit;
        display: flex;
        gap: 4px;
        align-items: center;
        white-space: nowrap;

        @media all and (max-width: 425px) {
            max-width: 150px;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .gw-wl-page-list__title-input-wrapper {
        & :global(.gw-wl-page-list__title-input) {
            border: none;
            background: none;
            color: var(--gw-lightgrey-text-color);
            border-bottom: 1px solid var(--gw-lightgrey-text-color);
            outline: none;
            box-shadow: none;
            font-size: 16px;
        }
        & :global(.--gw-with-error) {
            border: red 3px solid;
        }
    }

    .gw-wl-page-list__header :global(.gw-toggle-btn) {
        transform: rotateX(0deg);
        transition: transform var(--gw-default-transition-duration);
    }

    .gw-wl-page-list__header :global(.gw-toggle-btn-opened) {
        transform: rotateX(-180deg);
    }

    .gw-wl-page-list__items-wrapper {
        display: none;
        margin: 0;
        padding: 0;
    }

    .gw-wl-page-list__items-wrapper.gw-opened {
        display: block;
    }

    .gw-wl-page-list__items {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 20px;
        padding: 1em 0;
        margin: 0;

        @media all and (max-width: 768px) {
            grid-template-columns: repeat(3, 1fr);
        }

        @media all and (max-width: 480px) {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    .gw-wl-page-title-wrapper {
        display: flex;
        align-items: center;
        gap: 5px;
    }

    .gw-wl-page-list-header__line {
        height: 1px;
        width: 100%;
        flex: 1;
        background: #e6e8ec;
        transform: all 0.3s;

        &--denger {
            background: #ef466f;
        }
    }

    .gw-wl-page-list-content {
        position: relative;

        :global(.gw-wl-page-load-more) {
            margin-top: 4rem;
            padding: 12px 32px;
            font-weight: 700;
        }

        :global(.gw-wl-page-load-more.--outlined) {
            color: var(--gw-theme-main-text-primary);
        }
    }

    .gw-wl-page-action-overlay {
        padding: 16px 0;
        position: absolute;
        z-index: 10;
        top: 0;
        left: 0;
        width: 100%;
        background: #fff;
        box-shadow: 0 0 10px 10px #fff;
        transform: translateY(40px);
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s;
    }

    .gw-wl-page-action-overlay_visible {
        visibility: visible;
        opacity: 1;
        transform: none;
    }

    .gw-wl-page-delete-confirmation__title {
        font-size: 14px;
        font-style: normal;
        font-weight: 650;
        line-height: 20px;
        text-align: center;
        margin-bottom: 4px;
        color: var(--gw-dark-text-color);
    }

    .gw-wl-page-delete-confirmation__description {
        font-size: 10px;
        font-style: normal;
        line-height: 16px;
        text-align: center;
        margin-bottom: 8px;
        color: var(--gw-dark-text-color);
    }

    .gw-wl-page-delete-confirmation__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        justify-content: center;
        font-weight: 600;

        :global(.gw-wl-page-delete-confirmation__confirm-btn) {
            color: #ef466f;
        }

        :global(.gw-btn.gw-button) {
            font-weight: 600;
        }
    }
    .gw-wl-page-share-list {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        top: 100%;
        --gw-wl-share-channels-gap: 12px;

        &__title {
            font-size: 14px;
            font-weight: 600;
            line-height: 20px;
        }
    }

    .gw-wl-page-list-b2b {
        display: none;
        grid-template-columns: 3fr 2fr;
        grid-template-rows: auto auto auto;
        gap: 4px 8px;
        align-items: center;

        &.gw-opened {
            display: grid;
        }

        &__approve-status {
            grid-column: 1;
            grid-row: 1;
            font-size: 14px;
            line-height: 20px;
            justify-self: start;
        }

        &__total-price {
            grid-column: 1;
            grid-row: 2;
            font-size: 14px;
            line-height: 20px;
            justify-self: start;
        }

        &__send-notification {
            grid-column: 2;
            grid-row: 1;
            justify-self: end;
        }

        &__create-draft-order {
            grid-column: 2;
            grid-row: 2;
            justify-self: end;
        }

        &__approve-section {
            grid-column: 1 / -1;
            grid-row: 3;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            background-color: rgba(11, 30, 71, 0.06);
            border-radius: var(--gw-border-radius-lg);
            margin-top: 8px;

            &__info {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            &__action {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            :global(.gw-btn.gw-button) {
                color: #fff;
            }
        }
    }

    :global(.gw-btn.gw-button.gw-wl-button-with-icon) {
        display: flex;
        align-items: center;
        gap: 8px;
        white-space: nowrap;
        padding: 5px 10px;
    }
</style>
